import { useEffect, useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getTeamSettings, updateTeamSettings } from "services/team";
import Toast from "components/common/Toast";

// Sliders surfaced as "Coming soon" — only Specification is active today,
// matching the Configure Scoring Weightage modal.
const COMING_SOON_SLIDERS = [
  "Prosecution",
  "Precedential",
  "Dictionary",
  "Article Accuracy",
];

const getTeamId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedTeam") || "{}")
      ?.currentTeamId;
  } catch {
    return undefined;
  }
};

const getIsAdmin = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("selectedTeam") || "{}")?.currentRole ===
      "Admin"
    );
  } catch {
    return false;
  }
};

const DEFAULT_FIGURE_MATCH = 30;

const ScoreSettings = () => {
  const [specification, setSpecification] = useState(90);
  // Last-saved value, used to detect changes (dirty state) and to reset on Cancel.
  const [savedSpecification, setSavedSpecification] = useState(90);
  const [figureMatch, setFigureMatch] = useState(DEFAULT_FIGURE_MATCH);
  const [savedFigureMatch, setSavedFigureMatch] = useState(DEFAULT_FIGURE_MATCH);
  const [isSaving, setIsSaving] = useState(false);
  const isAdmin = getIsAdmin();

  // Load the team's current scoring weightage.
  useEffect(() => {
    const teamId = getTeamId();
    if (!teamId) return;
    (async () => {
      try {
        const res: any = await getTeamSettings(teamId);
        const settings = res?.data ?? res;
        if (typeof settings?.score_settings?.specification === "number") {
          setSpecification(settings.score_settings.specification);
          setSavedSpecification(settings.score_settings.specification);
        }
        if (typeof settings?.score_settings?.figure_match_threshold === "number") {
          setFigureMatch(settings.score_settings.figure_match_threshold);
          setSavedFigureMatch(settings.score_settings.figure_match_threshold);
        }
      } catch (error) {
        console.error("Error fetching score settings:", error);
      }
    })();
  }, []);

  const handleSave = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    setIsSaving(true);
    try {
      const res: any = await updateTeamSettings({
        team_id: teamId,
        score_settings: { specification, figure_match_threshold: figureMatch },
      });
      // Reflect the persisted value returned by the API.
      const saved = res?.data ?? res;
      if (typeof saved?.score_settings?.specification === "number") {
        setSpecification(saved.score_settings.specification);
        setSavedSpecification(saved.score_settings.specification);
      }
      if (typeof saved?.score_settings?.figure_match_threshold === "number") {
        setFigureMatch(saved.score_settings.figure_match_threshold);
        setSavedFigureMatch(saved.score_settings.figure_match_threshold);
      }
      Toast.success("Success", "Scoring weightage saved");
    } catch (error) {
      console.error("Error saving score settings:", error);
      Toast.error("Error", "Failed to save scoring weightage");
    } finally {
      setIsSaving(false);
    }
  };

  // Reset back to the last-saved value.
  const handleCancel = () => {
    setSpecification(savedSpecification);
    setFigureMatch(savedFigureMatch);
  };

  const isDirty =
    specification !== savedSpecification || figureMatch !== savedFigureMatch;
  const saveDisabled = !isDirty || isSaving;

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h4 className="text-lg font-interSemiBold text-[#101828] pb-0.5">
            Scoring weightage
          </h4>
          <p className="text-sm text-[#475467]">
            Adjust how each factor contributes to the overall coverage score.
          </p>
        </div>
        <div className="flex items-center gap-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="cancle-btn-blue !px-[15px] !text-sm hover:bg-[#F2F4F7]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveDisabled}
            className={`submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693] ${
              saveDisabled ? "button-disabled" : ""
            }`}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="w-full h-px bg-[#EAECF0] mb-6"></div>

      <div className="max-w-[620px]">
        {/* Score configuration is locked for now — entire section greyed out. */}
        <div className="flex flex-col gap-y-4 opacity-60 pointer-events-none select-none">
        {/* Specification (active) — inlined so the input keeps a stable
            identity across renders and stays draggable. */}
        <div className="mb-2.5">
          <div className="text-sm font-interRegular text-[#555F6D] mb-1.5">
            Specification
          </div>
          <div className="relative w-full cursor-default">
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              disabled
              value={specification}
              onChange={(e) => setSpecification(parseInt(e.target.value))}
              className="w-full h-2 cursor-not-allowed"
              style={{
                WebkitAppearance: "none",
                background: `linear-gradient(to right, #1751D0 ${specification}%, #E5E7EB ${specification}%)`,
                borderRadius: "9999px",
              }}
            />
          </div>
          <div className="flex items-center justify-end">
            <span className="text-sm font-interMedium text-[#4F4D55]">
              {specification}%
            </span>
          </div>
        </div>

        {COMING_SOON_SLIDERS.map((name) => (
          <div className="mb-6 disabled-rc-slider relative" key={name}>
            <span className="text-xs font-interMedium text-[#1751D0] bg-[#b5ccff80] border border-[#1751D0] px-2 py-0.5 rounded-full inline-block -top-2.5 right-4 absolute">
              Coming soon
            </span>
            <p className="text-sm font-interRegular text-[#555F6D] mb-3">
              {name}
            </p>
            <Slider />
            <div className="flex items-center justify-end mt-2">
              <span className="text-sm font-interMedium text-[#4F4D55]">
                0%
              </span>
            </div>
          </div>
        ))}
        </div>

        <div className="w-full h-px bg-[#EAECF0] my-6"></div>

        <div>
          <h4 className="text-lg font-interSemiBold text-[#101828] pb-0.5">
            Figure match threshold
          </h4>
          <p className="text-sm text-[#475467] mb-4">
            Minimum match confidence before a figure is assigned to a claim
            feature that has no reference numeral. Features scoring below the
            threshold keep their default figure.
          </p>
          <div
            className={
              isAdmin ? "" : "opacity-60 pointer-events-none select-none"
            }
          >
            <div className="relative w-full">
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                disabled={!isAdmin}
                value={figureMatch}
                onChange={(e) => setFigureMatch(parseInt(e.target.value))}
                className={`w-full h-2 ${
                  isAdmin ? "cursor-pointer" : "cursor-not-allowed"
                }`}
                style={{
                  WebkitAppearance: "none",
                  background: `linear-gradient(to right, #1751D0 ${figureMatch}%, #E5E7EB ${figureMatch}%)`,
                  borderRadius: "9999px",
                }}
              />
            </div>
            <div className="flex items-center justify-end">
              <span className="text-sm font-interMedium text-[#4F4D55]">
                {figureMatch}%
              </span>
            </div>
          </div>
          {!isAdmin && (
            <p className="text-xs text-[#98A2B3] mt-1">
              Only team admins can change this setting.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoreSettings;
